import { useState } from 'react';

export default function Topology() {
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'visual' | 'matrix'>('visual');

  const projects = [
    { id: '1', name: 'E-Commerce Platform' },
    { id: '2', name: 'Mobile API Gateway' },
    { id: '3', name: 'Analytics Engine' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">System Topology</h1>
            <p className="text-gray-600">Visualize your infrastructure dependencies</p>
          </div>

          <div className="flex items-center space-x-3">
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>

            <div className="flex items-center border border-gray-300 rounded-lg">
              <button
                onClick={() => setViewMode('visual')}
                className={`px-4 py-2 rounded-l-lg transition-colors ${
                  viewMode === 'visual'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-node-tree mr-2"></i>
                Visual
              </button>
              <button
                onClick={() => setViewMode('matrix')}
                className={`px-4 py-2 rounded-r-lg border-l border-gray-300 transition-colors ${
                  viewMode === 'matrix'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-table-line mr-2"></i>
                Matrix
              </button>
            </div>

            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <i className="ri-download-line mr-2"></i>
              Export
            </button>
          </div>
        </div>

        {/* Topology Visualization */}
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <i className="ri-zoom-in-line"></i>
              </button>
              <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <i className="ri-zoom-out-line"></i>
              </button>
              <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <i className="ri-fullscreen-line"></i>
              </button>
              <div className="h-6 w-px bg-gray-300 mx-2"></div>
              <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <i className="ri-layout-grid-line mr-2"></i>
                Auto Arrange
              </button>
            </div>

            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Healthy</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-gray-600">Warning</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">Critical</span>
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="p-8">
            {viewMode === 'visual' ? (
              <div className="h-[600px] flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <i className="ri-node-tree text-6xl text-gray-300 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Topology Visualization</h3>
                  <p className="text-gray-600 mb-4">Visual representation of system dependencies</p>
                  <div className="grid grid-cols-3 gap-8 max-w-4xl mx-auto">
                    {/* Sample nodes */}
                    <div className="space-y-4">
                      <div className="bg-white border-2 border-blue-500 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="font-medium text-sm">Web Server</span>
                        </div>
                        <div className="text-xs text-gray-500">server-01</div>
                      </div>
                      <div className="bg-white border-2 border-green-500 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="font-medium text-sm">API Gateway</span>
                        </div>
                        <div className="text-xs text-gray-500">api-01</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center">
                      <i className="ri-arrow-right-line text-4xl text-gray-300"></i>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-white border-2 border-purple-500 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="font-medium text-sm">Database</span>
                        </div>
                        <div className="text-xs text-gray-500">db-01</div>
                      </div>
                      <div className="bg-white border-2 border-orange-500 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span className="font-medium text-sm">Cache</span>
                        </div>
                        <div className="text-xs text-gray-500">cache-01</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[600px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-900 border-b">Component</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-900 border-b">Web Server</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-900 border-b">API Gateway</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-900 border-b">Database</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-900 border-b">Cache</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['Web Server', 'API Gateway', 'Database', 'Cache'].map((row, i) => (
                      <tr key={row} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{row}</td>
                        {[0, 1, 2, 3].map((col) => (
                          <td key={col} className="px-4 py-3 text-center">
                            {i !== col && (i === 0 || col === 2) ? (
                              <div className="w-6 h-6 bg-green-500 rounded-full mx-auto"></div>
                            ) : i !== col ? (
                              <div className="w-6 h-6 bg-gray-200 rounded-full mx-auto"></div>
                            ) : (
                              <div className="w-6 h-6 bg-blue-100 rounded-full mx-auto"></div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Total Nodes</div>
            <div className="text-2xl font-bold text-gray-900">24</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Dependencies</div>
            <div className="text-2xl font-bold text-gray-900">42</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Critical Paths</div>
            <div className="text-2xl font-bold text-red-600">3</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Avg Latency</div>
            <div className="text-2xl font-bold text-gray-900">245ms</div>
          </div>
        </div>
      </div>
    </div>
  );
}
